import { useState } from 'react';
import { FlaskConical } from 'lucide-react';
import { Button, Alert } from '@dreamtree-org/twreact-ui';
import { Field, textareaClass } from '@/components/workflow/inspectorFields';

/**
 * "Test step" — run the selected node on its own, against mock upstream state.
 *
 * The friction it removes: verifying one node's credentials, template or prompt meant
 * running the whole graph from the Start node and waiting for every step before it. On a
 * ten-step pipeline that is minutes per keystroke-sized change, which is why the raw-JSON
 * arguments textarea was so error-prone in the first place — nobody wants to pay that
 * round-trip to find a typo.
 *
 * It is a REAL execution (POST /api/workflows/test-node → engine.testNode), so it is only
 * ever fired by this button, never on render or on edit, and the panel says so before the
 * first click rather than after a surprising charge.
 */

/** Pre-fills the scope box with a stub per upstream node, so the shape the templates expect
 *  is visible rather than something the author has to remember and retype. */
function seedScope(upstreamIds) {
  if (!upstreamIds.length) return '{}';
  return JSON.stringify(
    Object.fromEntries(upstreamIds.map((id) => [id, { output: '' }])),
    null,
    2,
  );
}

/** Built outside JSX so the branch is a statement, not a nested template inside a template
 *  — and so "which branch did the Gate take" is only shown when it is not the default. */
function resultSummary(result) {
  const branch = result.branch && result.branch !== 'next' ? ` · branch: ${result.branch}` : '';
  return `ok · ${result.durationMs}ms${branch}`;
}

function ResultView({ result }) {
  if (!result) return null;
  if (!result.ok) {
    return (
      <Alert variant="error">
        <span className="font-mono text-[11px] break-words">{result.error}</span>
      </Alert>
    );
  }
  return (
    <div className="space-y-1">
      <div className="text-[10px] text-secondary-500">{resultSummary(result)}</div>
      <pre className="max-h-56 overflow-auto rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] p-2 text-[11px] font-mono whitespace-pre-wrap break-words">
        {JSON.stringify(result.output, null, 2)}
      </pre>
    </div>
  );
}

export default function TestStepPanel({ node, agentKey, upstreamIds = [], runTestNode }) {
  const [scopeText, setScopeText] = useState(() => seedScope(upstreamIds));
  const [inputText, setInputText] = useState('{}');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!runTestNode) return null;

  const parseOr = (text, label) => {
    try {
      const parsed = JSON.parse(text || '{}');
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('not an object');
      return { value: parsed };
    } catch {
      return { error: `${label} must be a JSON object.` };
    }
  };

  const run = async () => {
    const scope = parseOr(scopeText, 'Upstream output');
    const input = parseOr(inputText, 'Run input');
    if (scope.error || input.error) {
      setResult({ ok: false, error: scope.error || input.error });
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      setResult(await runTestNode({
        node: { id: node.id, type: node.type, data: node.data || {} },
        scope: scope.value,
        input: input.value,
        agent_key: agentKey || null,
      }));
    } catch (err) {
      // A transport/permission failure, not a node failure — the endpoint reports node
      // failures as {ok:false} bodies, so anything thrown here is about the request itself.
      setResult({ ok: false, error: err?.message || 'The test request failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 border-t border-[var(--app-border)] pt-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-secondary-500">Test step</span>
        <button type="button" onClick={() => setOpen((v) => !v)} className="text-[10px] text-primary-600">
          {open ? 'Hide' : 'Show'}
        </button>
      </div>
      {open && (
        <>
          {upstreamIds.length > 0 && (
            <Field label="Upstream output" hint="Stands in for earlier nodes, so {{node_id.output}} resolves.">
              <textarea className={textareaClass} rows={4} value={scopeText} onChange={(e) => setScopeText(e.target.value)} />
            </Field>
          )}
          <Field label="Run input" hint="What {{input.field}} resolves to.">
            <textarea className={textareaClass} rows={3} value={inputText} onChange={(e) => setInputText(e.target.value)} />
          </Field>
          <Button size="sm" variant="secondary" onClick={run} disabled={busy} className="w-full">
            <FlaskConical size={13} className="mr-1" />
            {busy ? 'Running…' : 'Run this step'}
          </Button>
          <p className="text-[10px] text-secondary-500">
            Runs for real: model tokens are spent and any endpoint this step calls is actually called.
          </p>
          <ResultView result={result} />
        </>
      )}
    </div>
  );
}
